/**
 * Instance-cluster panels (PXL): Housing + Workshop, and Endgame (Arena / Raid /
 * Season). Bind to the real RPCs. Arena/raid IN-MATCH combat is a documented
 * placeholder server-side (resolveMatch / resolveRaid, gaps D-7/D-8) — the
 * queue/lockout/reward framing here is real and "lights up" when they land.
 */

import { useState } from 'react';

import type { ArenaFormatWire } from '@great-cloud/shared/client';

import { useGame } from '../net-context.js';
import { DataView, Empty, IconGlyph, SectionTitle, Stat, Tabs } from '../widgets.js';
import { payForBatch } from './craftable.js';
import { humanizeId } from '../playerText.js';

export function HousingPanel(): JSX.Element {
  const { state, conn } = useGame();
  const [tab, setTab] = useState('house');
  const [acquire, setAcquire] = useState({ templateId: '', name: '' });
  const [houseId, setHouseId] = useState('');
  const [job, setJob] = useState({ houseId: '', recipeId: '', quantity: '1' });
  // The stacks a batch consumes, chosen like the craft panel's one-click list (the server
  // still judges ownership, station and rank); null = the bag cannot pay for it.
  const jobRecipe = state.recipes.find((r) => r.id === job.recipeId);
  const jobInputs = jobRecipe
    ? payForBatch(jobRecipe, state.inventory?.resources ?? [], Number(job.quantity))
    : null;
  const [jobId, setJobId] = useState('');

  return (
    <div>
      <Tabs
        tabs={[
          { id: 'house', label: 'Housing' },
          { id: 'workshop', label: 'Workshop' },
        ]}
        active={tab}
        onSelect={setTab}
      />
      {tab === 'house' ? (
        <div className="pxl-list">
          <button className="pxl-btn ghost" onClick={() => conn.send('houseList', {})}>
            refresh houses
          </button>
          {state.house ? <DataView value={state.house} /> : <Empty>No houses — acquire one.</Empty>}
          <SectionTitle>Acquire (Shard fee + upkeep sink)</SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              placeholder="template id"
              value={acquire.templateId}
              onChange={(e) => setAcquire({ ...acquire, templateId: e.target.value })}
            />
            <input
              className="pxl-input"
              style={{ width: 90 }}
              placeholder="name"
              value={acquire.name}
              onChange={(e) => setAcquire({ ...acquire, name: e.target.value })}
            />
            <button
              className="pxl-btn primary"
              disabled={!acquire.templateId}
              onClick={() =>
                conn.send('houseAcquire', {
                  templateId: acquire.templateId,
                  name: acquire.name || undefined,
                })
              }
            >
              acquire
            </button>
          </div>
          <SectionTitle>Enter / upkeep by house id</SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              type="number"
              placeholder="house id"
              value={houseId}
              onChange={(e) => setHouseId(e.target.value)}
            />
            <button
              className="pxl-btn"
              disabled={!houseId}
              onClick={() => conn.send('houseEnter', { houseId: Number(houseId) })}
            >
              enter
            </button>
            <button className="pxl-btn" onClick={() => conn.send('houseLeave', {})}>
              leave
            </button>
            <button
              className="pxl-btn"
              disabled={!houseId}
              onClick={() => conn.send('housePayUpkeep', { houseId: Number(houseId) })}
            >
              upkeep
            </button>
          </div>
          <div className="pxl-faint">
            Decoration placement, trophies, permissions + the ownsEntitlement display are wired on
            houseState (gap D-13 for the entitlement read).
          </div>
        </div>
      ) : null}
      {tab === 'workshop' ? (
        <div className="pxl-list">
          <button className="pxl-btn ghost" onClick={() => conn.send('workshopList', {})}>
            refresh jobs
          </button>
          {state.workshop ? <DataView value={state.workshop} /> : <Empty>No workshop jobs.</Empty>}
          <SectionTitle>Queue a job (offline crafting; same resolver)</SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input"
              style={{ width: 70 }}
              placeholder="house id"
              value={job.houseId}
              onChange={(e) => setJob({ ...job, houseId: e.target.value })}
            />
            <input
              className="pxl-input grow"
              placeholder="recipe id"
              value={job.recipeId}
              onChange={(e) => setJob({ ...job, recipeId: e.target.value })}
            />
            <input
              className="pxl-input"
              type="number"
              style={{ width: 50 }}
              value={job.quantity}
              onChange={(e) => setJob({ ...job, quantity: e.target.value })}
            />
            <button
              className="pxl-btn"
              disabled={!job.houseId || !job.recipeId || jobInputs === null}
              title={
                jobInputs === null && job.recipeId
                  ? 'your bag cannot pay for this recipe × quantity'
                  : undefined
              }
              onClick={() =>
                jobInputs !== null &&
                conn.send('workshopQueueJob', {
                  houseId: Number(job.houseId),
                  recipeId: job.recipeId,
                  inputs: jobInputs,
                  quantity: Number(job.quantity),
                })
              }
            >
              queue
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              type="number"
              placeholder="job id"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
            />
            <button
              className="pxl-btn"
              disabled={!jobId}
              onClick={() => conn.send('workshopCollect', { jobId: Number(jobId) })}
            >
              collect
            </button>
            <button
              className="pxl-btn"
              disabled={!jobId}
              onClick={() => conn.send('workshopCancel', { jobId: Number(jobId) })}
            >
              cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const FORMATS: ArenaFormatWire[] = ['duel', 'skirmish', 'team'];

export function EndgamePanel(): JSX.Element {
  const { state, conn } = useGame();
  const [tab, setTab] = useState('arena');
  const [encounter, setEncounter] = useState('');

  return (
    <div>
      <Tabs
        tabs={[
          { id: 'arena', label: 'Arena' },
          { id: 'raid', label: 'Raid' },
          { id: 'season', label: 'Season' },
        ]}
        active={tab}
        onSelect={setTab}
      />
      {tab === 'arena' ? (
        <div className="pxl-list">
          <button className="pxl-btn ghost" onClick={() => conn.send('arenaState', {})}>
            refresh
          </button>
          <Stat k="Status" v={state.arena?.status ?? '—'} />
          {(state.arena?.ratings ?? []).map((r) => (
            <div className="pxl-stat" key={r.format}>
              <span className="k">
                <IconGlyph code={r.format} assetRef="TGC-UI-001" size={18} /> {r.format}
              </span>
              <span className="v">
                {r.rating} ({r.rankTier}) · {r.wins}-{r.losses}
              </span>
            </div>
          ))}
          <SectionTitle>Queue (opt-in PvP)</SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            {FORMATS.map((f) => (
              <button
                key={f}
                className="pxl-btn grow"
                onClick={() => conn.send('arenaQueue', { format: f })}
              >
                {f}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="pxl-btn grow" onClick={() => conn.send('arenaLeave', {})}>
              leave queue
            </button>
            <button
              className="pxl-btn grow"
              onClick={() => conn.send('arenaLeaderboard', { format: 'duel' })}
            >
              leaderboard
            </button>
          </div>
          {state.arenaLeaderboard ? <DataView value={state.arenaLeaderboard} /> : null}
          <div className="pxl-faint">In-match scoreboard pending real resolveMatch (gap D-7).</div>
        </div>
      ) : null}
      {tab === 'raid' ? (
        <div className="pxl-list">
          <button className="pxl-btn ghost" onClick={() => conn.send('raidState', {})}>
            refresh lockouts
          </button>
          {(state.raid?.lockouts ?? []).map((l) => (
            <div className="pxl-stat" key={l.encounterId}>
              <span className="k">{humanizeId(l.encounterId)}</span>
              <span className="v">
                {l.lockedOut ? 'locked' : 'open'} {l.everCleared ? '· cleared' : ''}
              </span>
            </div>
          ))}
          <SectionTitle>Form a group</SectionTitle>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              placeholder="encounter id"
              value={encounter}
              onChange={(e) => setEncounter(e.target.value)}
            />
            <button
              className="pxl-btn primary"
              disabled={!encounter}
              onClick={() => conn.send('raidForm', { encounterId: encounter })}
            >
              form
            </button>
            <button className="pxl-btn" onClick={() => conn.send('raidLeave', {})}>
              leave
            </button>
          </div>
          <div className="pxl-faint">Encounter UI pending real resolveRaid (gap D-8).</div>
        </div>
      ) : null}
      {tab === 'season' ? (
        <div className="pxl-list">
          <button className="pxl-btn ghost" onClick={() => conn.send('seasonState', {})}>
            refresh
          </button>
          {state.season ? (
            <>
              <Stat
                k="Fee multiplier"
                v={`${state.season.feeMultiplier}× (festival holiday if <1)`}
              />
              <SectionTitle>Seasons</SectionTitle>
              {state.season.seasons.map((s) => (
                <div className="pxl-row" key={s.id} style={{ display: 'block' }}>
                  <div>
                    {s.name} <span className="pxl-badge">{s.active ? 'active' : 'ended'}</span>
                  </div>
                  <div className="pxl-faint">
                    {s.theme} · {s.activities.length} activities
                  </div>
                </div>
              ))}
              <SectionTitle>Earned ({state.season.earned.length})</SectionTitle>
              <DataView value={state.season.earned} />
            </>
          ) : (
            <Empty>No season data.</Empty>
          )}
          <div className="pxl-faint">Per-activity completion triggers are pending (gap D-14).</div>
        </div>
      ) : null}
    </div>
  );
}
